import { useMemo } from "react";
import { PlanTableFormat } from "../types/planning/plan.type";
import { shipmentFinalStatus } from "../types/status.type";

/**
 * Shipment statuses that make a DEPT route immutable

 */
const IMMUTABLE_SHIPMENT_STATUSES = Object.values(shipmentFinalStatus);

/**
 * Returns true if a route with status 'DEPT' and at least one shipment
 * in an immutable status (DLVR, DTCL, ARCS, ATMP, RTRN) cannot be updated.
 *
 * @param routeStatus - The status of the route
 * @param shipments - Array of shipments with their status
 * @returns true if route is immutable (cannot be modified)
 */
export function isRouteImmutablePure(
  routeStatus: string | null | undefined,
  shipments: Array<{ status: string }> | null | undefined
): boolean {
  return (
    routeStatus === "DEPT" &&
    Array.isArray(shipments) &&
    shipments.some((s) =>
      IMMUTABLE_SHIPMENT_STATUSES.includes(
        s.status as (typeof IMMUTABLE_SHIPMENT_STATUSES)[number]
      )
    )
  );
}

/**
 * Returns the display label of immutable statuses found in a route's shipments.
 */
export function getImmutableStatus(
  shipments: Array<{ status: string }> | null | undefined
): string {
  if (!Array.isArray(shipments)) return "DLVR";
  const found = [
    ...new Set(
      shipments
        .map((s) => s.status)
        .filter((s): s is string =>
          IMMUTABLE_SHIPMENT_STATUSES.includes(
            s as (typeof IMMUTABLE_SHIPMENT_STATUSES)[number]
          )
        )
    ),
  ];
  return found.length > 0 ? found.join(", ") : "DLVR";
}

export function isRouteClosedForSequencing(route: PlanTableFormat[]): boolean {
  if (route.length === 0) return false;
  return route.some((r) => ["CLDV", "DISP", "DEPT"].includes(r.status));
}

/**
 * Hook version: applies the immutability logic
 */
export function useIsRouteImmutable(
  routeStatus: string | null | undefined,
  shipments: Array<{ status: string }> | null | undefined
): boolean {
  return useMemo(
    () => isRouteImmutablePure(routeStatus, shipments),
    [routeStatus, shipments]
  );
}
